<template>
  <div class="page-container home-container">
    <div :class="['split', active && 'focus-' + active]">
      <div class="header-slot">
        <Header />
      </div>

      <section class="side studio-side" @mouseenter="active = 'left'" @mouseleave="active = null">
        <div class="side-inner">
          <p class="eyebrow">Game studio</p>
          <h1 class="studio-title">Fluorine Republika</h1>
          <p class="studio-line studio-tagline">We dissolve the bad parts of games. Not the fun.</p>
          <p class="studio-stance">No forced ads. Ever. You pay for the game, new chapters, or looks. Never for power.</p>
          <div class="game-cards">
            <router-link v-for="game in games" :key="game.slug" class="game-card" :to="{ name: 'FluorineRepublika', hash: '#' + game.slug }">
              <div class="game-card-image">
                <img v-if="game.image" :src="game.image" :alt="game.name" :class="game.imageFit">
                <span v-else class="game-card-initial">{{ (game.shortName || game.name).charAt(0) }}</span>
              </div>
              <h3>{{ game.shortName || game.name }} <span v-if="game.codename" class="working-title" title="Working title. The real name comes at launch.">Codename</span></h3>
              <p>{{ game.tagline }}</p>
              <span class="badge">{{ game.status }} · <PlatformList :platforms="game.platforms" /></span>
            </router-link>
          </div>
          <router-link class="studio-cta" :to="{ name: 'FluorineRepublika' }">Visit the studio →</router-link>
        </div>
      </section>

      <section class="side portfolio-side" @mouseenter="active = 'right'" @mouseleave="active = null">
        <div class="side-inner">
          <p class="eyebrow portfolio-eyebrow">Portfolio</p>
          <h2 class="portfolio-title">Other work</h2>
          <p class="portfolio-line">Photography, 3D, printing, code, video and graphic design.</p>
          <div class="thumbnails-container">
            <div class="thumbnail" v-for="section in sections" :key="section.route" @click="navigateTo(section.route)">
              <video v-if="section.video" autoplay muted loop playsinline :poster="section.image" :aria-label="section.label">
                <source :src="section.video.webm" type="video/webm">
                <source :src="section.video.mp4" type="video/mp4">
              </video>
              <img v-else :src="section.image" :alt="section.label" />
              <p>{{ section.label }}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
    <SiteFooter />
  </div>
</template>

<script>
import Header from './header.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import PlatformList from '@/components/PlatformList.vue';
import games from '@/data/games';
import otherWork from '@/data/otherWork';

export default {
  name: 'HomePage',
  components: {
    Header,
    SiteFooter,
    PlatformList
  },
  data() {
    return {
      games,
      sections: otherWork,
      active: null // 'left' or 'right' while that half is hovered
    };
  },
  methods: {
    navigateTo(routeName) {
      this.$router.push({ name: routeName });
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../assets/styles/global.scss' as *;
@use '../assets/styles/studio.scss' as *;

.home-container {
  overflow-x: hidden;
}
.split {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 100vh;
  flex-shrink: 0;
}
.header-slot {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 60;
  display: flex;
  justify-content: center;
  pointer-events: none;
  > * {
    pointer-events: auto;
  }
}
.side {
  flex: 1 1 0;
  min-width: 0;
  box-sizing: border-box;
  padding: 120px 40px 48px;
  display: flex;
  justify-content: center;
  transition: flex-grow 0.6s ease, filter 0.6s ease, opacity 0.6s ease, box-shadow 0.6s ease;
}
.side-inner {
  width: 100%;
  max-width: 620px;
  transition: transform 0.6s ease;
}

// Hovering one half pops it forward a little and blurs the other one.
@media (hover: hover) and (min-width: 769px) {
  .focus-left .studio-side,
  .focus-right .portfolio-side {
    flex-grow: 1.15;
    z-index: 1;
    .side-inner {
      transform: scale(1.03);
    }
  }
  .focus-left .studio-side {
    box-shadow: 12px 0 40px rgba(0, 0, 0, 0.45);
  }
  .focus-right .portfolio-side {
    box-shadow: -12px 0 40px rgba(0, 0, 0, 0.25);
  }
  .focus-left .portfolio-side,
  .focus-right .studio-side {
    filter: blur(3px);
  }
}

/* Left: the studio */
.studio-side {
  background: var(--fluorine-dark);
  color: var(--fluorine-text);
  text-align: center;
}
.game-cards {
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}
.game-card-image {
  height: 140px;
}
.studio-cta {
  display: inline-block;
  margin-top: 24px;
  color: var(--fluorine);
  font-size: 20px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}

/* Right: the portfolio */
.portfolio-side {
  background: var(--white);
  color: var(--black);
  text-align: center;
}
.portfolio-eyebrow {
  color: var(--grayblue);
}
.portfolio-title {
  margin: 8px 0;
  font-family: "Times New Roman";
  font-size: 48px;
  font-weight: 400;
  color: var(--black);
}
.portfolio-line {
  margin: 0 auto 24px;
  max-width: 600px;
  font-family: "PT Sans Narrow";
  font-size: 18px;
}
// Always 3 x 2 (2 x 3 on phones), so the six tiles never end up 5 + 1.
.thumbnails-container {
  display: grid;
  grid-template-columns: repeat(3, auto);
  justify-content: center;
}
.thumbnail {
  width: 170px;
  height: 250px;
  border-radius: 20px;
  overflow: hidden;
  text-align: center;
  margin: 10px;
  transition: transform 0.3s ease;
  cursor: pointer;
}
.thumbnail:hover {
  transform: scale(1.1);
}
.thumbnail img,
.thumbnail video {
  display: block;
  width: 100%;
  height: 80%;
  object-fit: cover;
}
.thumbnail p {
  margin: 10px 0 0 0;
  font-size: 16px;
  color: #333;
}

@media (max-width: 768px) {
  .split {
    flex-direction: column;
  }
  .header-slot {
    position: static;
    background: var(--fluorine-dark);
  }
  .side {
    padding: 32px 16px;
  }
  .studio-side {
    padding-top: 16px;
  }
  .studio-title,
  .portfolio-title {
    font-size: 34px;
  }
  .thumbnails-container {
    grid-template-columns: repeat(2, auto);
  }
  .thumbnail {
    width: 140px;
    height: 210px;
  }
}
</style>
